import type { StaffMemberListItem } from "@wallyo/contracts/console";
import type { AccountSearchResult } from "@wallyo/contracts/console/accounts";
import {
  SUPPORT_CASE_PRIORITIES,
  type SupportCaseDetail,
  type SupportCasePriority,
} from "@wallyo/contracts/console/cases";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { ConsoleClient } from "../../api/client";
import { listRoles, listStaff, searchAccounts } from "../../api/console-api";
import { useApi } from "../../api/context";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { describeKind, nameOf } from "../customers/customer-format";
import { queryProblem } from "../customers/search-state";
import { Modal } from "../team/TeamDialogs";
import { CASE_FEATURES } from "./case-access";
import { PRIORITY_LABELS, customerLabel } from "./case-format";
import { CaseFailureNotice } from "./CasePanelView";
import {
  EMPTY_CALL,
  describeCaseFailure,
  idProblem,
  newCallProblem,
  reasonProblem,
  sendClose,
  sendLink,
  sendOpenCase,
  sendReassign,
  type CaseFailure,
  type NewCallFields,
} from "./case-writes";

/**
 * One write intent in a dialog: the same Idempotency-Key until the person
 * changes what they ask for (any field edit) or it succeeds. A failure keeps
 * the key, so "try again" after a lost answer can't do it twice.
 */
function useCaseWrite() {
  const intent = useRef(createIntent());
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<CaseFailure | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function run<T>(check: string | null, send: (key: string) => Promise<T>, done: (value: T) => void) {
    setProblem(check);
    if (check) return;
    setPending(true);
    setFailure(null);
    try {
      const value = await send(intent.current.key());
      intent.current.reset();
      done(value);
    } catch (error) {
      setFailure(describeCaseFailure(error));
    } finally {
      setPending(false);
    }
  }

  const edited = () => {
    intent.current.reset();
    setProblem(null);
  };

  return { pending, failure, problem, run, edited };
}

type DialogFrameProps = {
  title: string;
  children: ReactNode;
  submitLabel: string;
  danger?: boolean;
  pending: boolean;
  problem: string | null;
  failure: CaseFailure | null;
  onSubmit: () => void;
  onClose: () => void;
  onReload?: () => void;
};

function DialogFrame(props: DialogFrameProps) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <Modal title={props.title} onClose={props.onClose}>
      <form className="modal-form" onSubmit={submit} noValidate>
        {props.children}
        {props.problem ? <p className="field-problem">{props.problem}</p> : null}
        {props.failure ? <CaseFailureNotice failure={props.failure} onReload={props.onReload} /> : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={props.onClose}>
            {t("cases.cancel")}
          </button>
          <button className={`btn ${props.danger ? "danger" : "primary"}`} type="submit" disabled={props.pending}>
            {props.pending ? t("cases.sending") : props.submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="case-reason">{t("cases.reason")}</label>
      <textarea id="case-reason" rows={2} value={value} onChange={(event) => onChange(event.target.value)} />
      <span className="meta">{t("cases.reasonHint")}</span>
    </div>
  );
}

type CaseDialogProps = {
  detail: SupportCaseDetail;
  onClose: () => void;
  /** The case as the server answered it. */
  onDone: (detail: SupportCaseDetail, notice: string) => void;
  /** The case changed meanwhile: close and read it again. */
  onReload: () => void;
};

// ── Réattribuer ──────────────────────────────────────────────────────────

type StaffChoices =
  | { status: "loading" }
  | { status: "ready"; members: StaffMemberListItem[] }
  | { status: "failed"; error: unknown };

/** Active members who can work cases (their roles hold CLAIM_CASES), except the current owner. */
export function reassignCandidates(
  staff: readonly StaffMemberListItem[],
  caseRoleIds: ReadonlySet<string> | null,
  ownerId: string | null,
): StaffMemberListItem[] {
  return staff.filter(
    (member) =>
      member.status === "active" &&
      member.staffMemberId !== ownerId &&
      (caseRoleIds === null || member.roles.some((role) => caseRoleIds.has(role.staffRoleId))),
  );
}

async function loadCandidates(client: ConsoleClient, ownerId: string | null): Promise<StaffMemberListItem[]> {
  const [staff, roles] = await Promise.all([listStaff(client), listRoles(client).catch(() => null)]);
  const caseRoleIds = roles
    ? new Set(
        roles.roles
          .filter((role) => role.features.some((feature) => feature.settingId === CASE_FEATURES.work))
          .map((role) => role.staffRoleId),
      )
    : null;
  return reassignCandidates(staff.staffMembers, caseRoleIds, ownerId);
}

export function ReassignDialog(props: CaseDialogProps & { staffPicker: boolean }) {
  const { client } = useApi();
  const write = useCaseWrite();
  const [reason, setReason] = useState("");
  const [toId, setToId] = useState("");
  const [choices, setChoices] = useState<StaffChoices>({ status: "loading" });
  const [reads, setReads] = useState(0);
  const ownerId = props.detail.owner?.staffMemberId ?? null;

  useEffect(() => {
    if (!props.staffPicker) return;
    let current = true;
    setChoices({ status: "loading" });
    loadCandidates(client, ownerId).then(
      (members) => current && setChoices({ status: "ready", members }),
      (error: unknown) => current && setChoices({ status: "failed", error }),
    );
    return () => {
      current = false;
    };
  }, [client, ownerId, props.staffPicker, reads]);

  const picker = props.staffPicker && choices.status !== "failed";
  const pick = (next: string) => {
    write.edited();
    setToId(next);
  };
  return (
    <DialogFrame
      title={t("cases.reassign.title", { reference: props.detail.reference })}
      submitLabel={t("cases.reassign.submit")}
      pending={write.pending}
      problem={write.problem}
      failure={write.failure}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          !toId.trim()
            ? t("cases.problem.pickStaff")
            : ((picker ? null : idProblem(toId, "cases.problem.staffId")) ?? reasonProblem(reason)),
          (key) => sendReassign(client, props.detail, { toStaffMemberId: toId, reason }, key),
          ({ supportCase }) =>
            props.onDone(supportCase, t("cases.reassign.done", { name: supportCase.owner?.name ?? "" })),
        )
      }
    >
      <p className="modal-body">
        {props.detail.owner
          ? t("cases.reassign.from", { name: props.detail.owner.name })
          : t("cases.reassign.unassigned")}
      </p>
      {picker ? (
        <div className="field">
          <label htmlFor="case-staff">{t("cases.reassign.to")}</label>
          {choices.status === "loading" ? (
            <span className="meta" role="status">
              {t("cases.reassign.loading")}
            </span>
          ) : (
            <select id="case-staff" value={toId} onChange={(event) => pick(event.target.value)}>
              <option value="">{t("cases.reassign.choose")}</option>
              {choices.status === "ready"
                ? choices.members.map((member) => (
                    <option key={member.staffMemberId} value={member.staffMemberId}>
                      {`${member.name} · ${member.roles.map((role) => role.name).join(", ")}`}
                    </option>
                  ))
                : null}
            </select>
          )}
        </div>
      ) : (
        <div className="field">
          {choices.status === "failed" && props.staffPicker ? (
            <>
              <ErrorNotice error={choices.error} />
              <div>
                <button className="btn small" type="button" onClick={() => setReads((count) => count + 1)}>
                  {t("cases.retry")}
                </button>
              </div>
            </>
          ) : null}
          <label htmlFor="case-staff-id">{t("cases.reassign.toId")}</label>
          <input
            id="case-staff-id"
            className="mono"
            dir="ltr"
            value={toId}
            onChange={(event) => pick(event.target.value)}
          />
          <span className="meta">{t("cases.reassign.idHint")}</span>
        </div>
      )}
      <ReasonField
        value={reason}
        onChange={(next) => {
          write.edited();
          setReason(next);
        }}
      />
    </DialogFrame>
  );
}

// ── Clore ────────────────────────────────────────────────────────────────

export function CloseDialog(props: CaseDialogProps) {
  const { client } = useApi();
  const write = useCaseWrite();
  const [reason, setReason] = useState("");
  return (
    <DialogFrame
      title={t("cases.close.title", { reference: props.detail.reference })}
      submitLabel={t("cases.close.submit")}
      danger
      pending={write.pending}
      problem={write.problem}
      failure={write.failure}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reasonProblem(reason),
          (key) => sendClose(client, props.detail, reason, key),
          ({ supportCase }) => props.onDone(supportCase, t("cases.close.done", { reference: supportCase.reference })),
        )
      }
    >
      <p className="modal-body">{t("cases.close.body")}</p>
      <ReasonField
        value={reason}
        onChange={(next) => {
          write.edited();
          setReason(next);
        }}
      />
    </DialogFrame>
  );
}

// ── Lier à un client ─────────────────────────────────────────────────────

type SearchResults =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; results: readonly AccountSearchResult[] }
  | { status: "failed"; error: unknown };

/**
 * Picks a customer: a search of accounts with SEARCH_ACCOUNTS, else their
 * account id typed in. Contact details come back masked, as everywhere.
 */
export function CustomerPicker({
  canSearch,
  userId,
  onPick,
}: {
  canSearch: boolean;
  userId: string;
  onPick: (userId: string) => void;
}) {
  const { client } = useApi();
  const [query, setQuery] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResults>({ status: "idle" });
  const generation = useRef(0);

  if (!canSearch) {
    return (
      <div className="field">
        <label htmlFor="case-customer-id">{t("cases.customer.id")}</label>
        <input
          id="case-customer-id"
          className="mono"
          dir="ltr"
          value={userId}
          onChange={(event) => onPick(event.target.value)}
        />
        <span className="meta">{t("cases.customer.idHint", { feature: CASE_FEATURES.searchAccounts })}</span>
      </div>
    );
  }

  const search = () => {
    const trimmed = query.trim();
    const bad = queryProblem(trimmed);
    setProblem(bad);
    if (bad) return;
    const mine = ++generation.current;
    setResults({ status: "loading" });
    searchAccounts(client, { query: trimmed }).then(
      (page) => generation.current === mine && setResults({ status: "ready", results: page.results }),
      (error: unknown) => generation.current === mine && setResults({ status: "failed", error }),
    );
  };

  return (
    <div className="field customer-picker">
      <label htmlFor="case-customer-query">{t("cases.customer.search")}</label>
      <div className="search-row">
        <input
          id="case-customer-query"
          value={query}
          placeholder={t("cases.customer.placeholder")}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              search();
            }
          }}
        />
        <button className="btn" type="button" onClick={search} disabled={results.status === "loading"}>
          {t("cases.customer.searchButton")}
        </button>
      </div>
      {problem ? <p className="field-problem">{problem}</p> : null}
      {results.status === "loading" ? (
        <span className="meta" role="status">
          {t("cases.customer.searching")}
        </span>
      ) : null}
      {results.status === "failed" ? <ErrorNotice error={results.error} /> : null}
      {results.status === "ready" ? (
        results.results.length === 0 ? (
          <span className="meta">{t("cases.customer.none")}</span>
        ) : (
          <ul className="pick-list" role="radiogroup" aria-label={t("cases.customer.results")}>
            {results.results
              .filter((result) => !result.deleted)
              .map((result) => (
                <li key={result.userId}>
                  <label>
                    <input
                      type="radio"
                      name="case-customer"
                      value={result.userId}
                      checked={userId === result.userId}
                      onChange={() => onPick(result.userId)}
                    />{" "}
                    <b>
                      <bdi>{nameOf(result)}</bdi>
                    </b>{" "}
                    <span className="meta">
                      {describeKind(result)}
                      {result.maskedPhone ? (
                        <>
                          {" · "}
                          <span className="mono">{result.maskedPhone}</span>
                        </>
                      ) : null}
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export function LinkDialog(props: CaseDialogProps & { canSearch: boolean }) {
  const { client } = useApi();
  const write = useCaseWrite();
  const [reason, setReason] = useState("");
  const [userId, setUserId] = useState("");
  return (
    <DialogFrame
      title={t("cases.link.title", { reference: props.detail.reference })}
      submitLabel={t("cases.link.submit")}
      pending={write.pending}
      problem={write.problem}
      failure={write.failure}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          !userId.trim()
            ? t("cases.problem.pickCustomer")
            : (idProblem(userId, "cases.problem.customerId") ?? reasonProblem(reason)),
          (key) => sendLink(client, props.detail, { userId, reason }, key),
          ({ supportCase }) => props.onDone(supportCase, t("cases.link.done", { name: customerLabel(supportCase) })),
        )
      }
    >
      <p className="modal-body">
        {props.detail.customer
          ? t("cases.link.current", { name: customerLabel(props.detail) })
          : props.detail.maskedContactPhone
            ? t("cases.link.caller", { phone: props.detail.maskedContactPhone })
            : t("cases.link.body")}
      </p>
      <CustomerPicker
        canSearch={props.canSearch}
        userId={userId}
        onPick={(next) => {
          write.edited();
          setUserId(next);
        }}
      />
      <ReasonField
        value={reason}
        onChange={(next) => {
          write.edited();
          setReason(next);
        }}
      />
    </DialogFrame>
  );
}

// ── + Noter un appel ─────────────────────────────────────────────────────

/** Logs a call and opens a case for it: for a known customer (searched) or an unknown caller (their number). */
export function NewCallDialog({
  canSearch,
  onClose,
  onOpened,
}: {
  canSearch: boolean;
  onClose: () => void;
  onOpened: (detail: SupportCaseDetail) => void;
}) {
  const { client } = useApi();
  const write = useCaseWrite();
  const [fields, setFields] = useState<NewCallFields>(EMPTY_CALL);
  const set = (change: Partial<NewCallFields>) => {
    write.edited();
    setFields((current) => ({ ...current, ...change }));
  };
  return (
    <DialogFrame
      title={t("cases.newCall.title")}
      submitLabel={t("cases.newCall.submit")}
      pending={write.pending}
      problem={write.problem}
      failure={write.failure}
      onClose={onClose}
      onSubmit={() =>
        void write.run(
          newCallProblem(fields),
          (key) => sendOpenCase(client, fields, key),
          ({ supportCase }) => onOpened(supportCase),
        )
      }
    >
      <fieldset className="reply-channels">
        <legend>{t("cases.newCall.who")}</legend>
        <label>
          <input
            type="radio"
            name="caller"
            checked={fields.caller === "customer"}
            onChange={() => set({ caller: "customer" })}
          />{" "}
          {t("cases.newCall.known")}
        </label>
        <label>
          <input
            type="radio"
            name="caller"
            checked={fields.caller === "unknown"}
            onChange={() => set({ caller: "unknown" })}
          />{" "}
          {t("cases.newCall.unknown")}
        </label>
      </fieldset>
      {fields.caller === "customer" ? (
        <CustomerPicker canSearch={canSearch} userId={fields.userId} onPick={(userId) => set({ userId })} />
      ) : (
        <div className="field">
          <label htmlFor="case-caller-phone">{t("cases.newCall.phone")}</label>
          <input
            id="case-caller-phone"
            className="mono"
            dir="ltr"
            inputMode="tel"
            value={fields.phone}
            onChange={(event) => set({ phone: event.target.value })}
          />
          <span className="meta">{t("cases.newCall.phoneHint")}</span>
        </div>
      )}
      <div className="field">
        <label htmlFor="case-subject">{t("cases.newCall.subject")}</label>
        <input id="case-subject" value={fields.subject} onChange={(event) => set({ subject: event.target.value })} />
      </div>
      <div className="field">
        <label htmlFor="case-summary">{t("cases.call.label")}</label>
        <textarea
          id="case-summary"
          rows={3}
          value={fields.body}
          onChange={(event) => set({ body: event.target.value })}
        />
        <span className="meta">{t("cases.newCall.internal")}</span>
      </div>
      <div className="form">
        <div className="field">
          <label htmlFor="case-minutes">{t("cases.call.minutes")}</label>
          <input
            id="case-minutes"
            inputMode="numeric"
            value={fields.minutes}
            onChange={(event) => set({ minutes: event.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="case-priority">{t("cases.newCall.priority")}</label>
          <select
            id="case-priority"
            value={fields.priority}
            onChange={(event) => set({ priority: event.target.value as SupportCasePriority })}
          >
            {SUPPORT_CASE_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {t(PRIORITY_LABELS[priority])}
              </option>
            ))}
          </select>
        </div>
      </div>
    </DialogFrame>
  );
}
